/**
 * AGENT OVERVIEW (2026-11-01).
 *
 * The first thing an agent opens. It answers three questions in order: what have I earned, what is still
 * owed to me, and can I actually be paid yet. Everything else is a link away.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  AgentPageState,
  Badge,
  formatDate,
  money,
  payoutTone,
  styles,
  useAgentResource,
} from "./agentShared";

function Stat({ label, value, hint }) {
  return (
    <div style={styles.stat}>
      <span style={styles.statLabel}>{label}</span>
      <div style={styles.statValue}>{value}</div>
      {hint ? <span style={styles.muted}>{hint}</span> : null}
    </div>
  );
}

export default function AgentOverview() {
  const { loading, error, unavailable, data, reload } = useAgentResource(
    "/api/platform-plans/referral/summary",
  );
  const [copied, setCopied] = useState("");

  const copy = async (value) => {
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const el = document.createElement("textarea");
        el.value = value;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied("copied");
      window.setTimeout(() => setCopied(""), 2000);
    } catch (_) {
      setCopied("");
    }
  };

  return (
    <AgentPageState
      loading={loading}
      error={error}
      unavailable={unavailable}
      reload={reload}
    >
      {data ? (
        <div style={styles.page}>
          <h1 style={styles.h1}>
            Welcome back{data.agent?.displayName ? `, ${data.agent.displayName}` : ""}
          </h1>
          <p style={{ ...styles.muted, marginTop: 0 }}>
            You earn {data.agent?.commissionPercent}% of what OGESTYLE collects from
            each business you bring in, net of card fees.
          </p>

          {!data.connect?.payoutsEnabled ? (
            <div style={styles.notice}>
              <strong>Payouts aren’t set up yet.</strong> Your commission is being
              recorded, but it can’t reach your bank until you add a bank account.{" "}
              <Link to="/dashboard/agent/payouts">Set up payouts →</Link>
            </div>
          ) : null}

          <div style={styles.statGrid}>
            <Stat
              label="Earned to date"
              value={money(data.totals?.earnedCents)}
              hint={`${data.totals?.commissionCount || 0} commission${
                data.totals?.commissionCount === 1 ? "" : "s"
              }`}
            />
            <Stat
              label="Awaiting payment"
              value={money(data.totals?.pendingCents)}
              hint="Recorded, not yet sent"
            />
            <Stat
              label="Active referrals"
              value={data.totals?.activeReferrals || 0}
              hint={`${data.totals?.referrals || 0} total`}
            />
            <Stat
              label="Commission rate"
              value={`${data.agent?.commissionPercent ?? "—"}%`}
              hint={
                data.agent?.commissionCapMonths
                  ? `For ${data.agent.commissionCapMonths} months per business`
                  : "Open-ended"
              }
            />
          </div>

          <div style={styles.card}>
            <h2 style={styles.h2}>Your link</h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              <input
                readOnly
                value={data.agent?.link || ""}
                style={{ ...styles.input, flex: "1 1 320px" }}
                aria-label="Your referral link"
              />
              <button
                type="button"
                style={styles.btn}
                onClick={() => copy(data.agent?.link)}
              >
                {copied ? "Copied" : "Copy link"}
              </button>
              <Link to="/dashboard/agent/links" style={styles.btnGhost}>
                More ways to share
              </Link>
            </div>
            <p style={{ ...styles.muted, marginBottom: 0 }}>
              Anyone who signs up through it is yours — the code is{" "}
              <strong>{data.agent?.code}</strong>.
            </p>
          </div>

          <div style={styles.card}>
            <h2 style={styles.h2}>Recent commission</h2>
            {Array.isArray(data.recentCommissions) &&
            data.recentCommissions.length > 0 ? (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Commission</th>
                    <th style={styles.th}>Rate</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentCommissions.map((row) => (
                    <tr key={row.id}>
                      <td style={styles.td}>{formatDate(row.at)}</td>
                      <td style={styles.td}>
                        <strong>{money(row.cents)}</strong>
                      </td>
                      <td style={styles.td}>{row.percent}%</td>
                      <td style={styles.td}>
                        <Badge tone={payoutTone(row.status)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={styles.empty}>
                No commission yet. Commission starts once a business you referred
                pays their first invoice — nothing is earned during their trial.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </AgentPageState>
  );
}
